import { useTranslation } from 'react-i18next';
import { Tab } from '@headlessui/react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';

import { Breadcrumbs } from '../../../components/Breadcrumbs';
import { General } from './General';
import { Hours } from './Hours';
import { Settings } from './Settings';
import { Delivery } from './Delivery';
import { POS } from '../../POS/POS';
import { Tables } from './Tables';
import Loading from '../../../components/Loading';
import Button from '../../../components/Button/Button';
import {
    useEditStore,
    useEditStoreAddons,
    useGetStore,
    useGetStoreIntegrations,
    useGetStoreTables,
    useSetStoreTimes
} from '../../../network';
import Discounts from '../Discounts/Discounts';

export const StoreSettingsPage = () => {
    const { t } = useTranslation();
    const { slug } = useParams();

    // hooks for manipulating async data
    const { data: integrations = {} } = useGetStoreIntegrations({ slug });
    const { data: store = {}, isLoading: isStoreLoading } = useGetStore({ slug });
    const { isInitialLoading: isTableLoading } = useGetStoreTables({ slug, enabled: !!integrations?.pos_selected?.params?.terminal_group_id });
    const { mutateAsync: editStore, isLoading: isEditStore } = useEditStore();
    const { mutateAsync: editStoreAddons } = useEditStoreAddons();
    const { mutateAsync: setStoreTimes, isLoading: isSetStoreTime } = useSetStoreTimes();

    const loading = isTableLoading || isStoreLoading || isEditStore || isSetStoreTime;
    const isDisabled = isStoreLoading || isEditStore || isSetStoreTime;

    const validationSchema = yup.object().shape({
        name: yup.string().required(t('Required!')),
        address: yup.string().required(t('Required!')),
        time_zone_name: yup.string().required(t('Required!')),
    });

    const formik = useFormik({
        initialValues: {
            name: store.name || '',
            email: '',
            address: store.address || '',
            time_zone_name: store.time_zone_name || '',
            comment: '',
            service_charge_percent: store.service_charge_percent || '',
            service_charge_round: store.service_charge_round || '',
            has_delivery: store.has_delivery || false,
            has_dine_in: store.has_dine_in || false,
            has_takeaway: store.has_takeaway || false,
            times: store.times || {},
            tips_allowed: store.tips_allowed || false,
            order_notes_allowed: store.order_notes_allowed || false,
            cutlery_allowed: store.cutlery_allowed || false,
            lng: store.point?.lng || '',
            lat: store.point?.lat || '',
            postCode: '',
            country: '',
            phoneCode: '',
            phoneNumber: '',
        },
        enableReinitialize: true,
        validateOnChange: false,
        validateOnBlur: false,
        validationSchema: validationSchema,
        onSubmit: async (data) => {
            const { tips_allowed, order_notes_allowed, cutlery_allowed  } = data;

            await editStoreAddons({ id: slug, params: { tips_allowed, order_notes_allowed, cutlery_allowed  } });
            await setStoreTimes({ id: slug, times: data.times });
            await editStore({ id: slug, params: data });
        },
    });

    const props = { formik, loading };

    const tabs = [
        {
            name: `${t('General')}`,
            component: <General {...props} />,
        },
        {
            name: `${t('Operating Hours')}`,
            component: <Hours {...props} />,
        },
        {
            name: `${t('Settings')}`,
            component: <Settings {...props} />,
        },
        {
            name: `${t('Delivery')}`,
            component: <Delivery {...props} />,
        },
        {
            name: `POS`,
            component: <POS />,
        },
        {
            name: `Discounts`,
            component: <Discounts />,
        },
        {
            name: `${t('Tables')}`,
            component: <Tables {...props} />,
        },
    ];

    // links for breadcrumbs
    const pages = [
        { name: t('Stores'), path: '/chain/stores' },
        {
            name: store.name,
            path: `/chain/stores/${slug}`,
        },
        { name: t('Editing'), path: `/chain/stores/${slug}/edit` },
    ];

    return (
        <>
            <div className="flex justify-between max-w-5xl items-baseline">
                <Breadcrumbs pages={pages} />

                <Button type="submit" onClick={formik.handleSubmit} isUpperCase={false} isLoading={isDisabled} disabled={isDisabled} className="h-9">
                    {t('Save')}
                </Button>
            </div>
            <Loading loading={loading}>
                <div className="w-full mb-5">
                    <Tab.Group>
                        <Tab.List className="flex max-w-5xl rounded-lg shadow-md bg-gray-100">
                            {tabs.map(({ name }) => (
                                <Tab
                                    key={name}
                                    className={({ selected }) =>
                                        classNames(
                                            'w-full first:rounded-l-2xl tabs p-2 leading-5 text-xs font-medium',
                                            'ring-white font-extrabold focus:outline-none whitespace-nowrap',
                                            selected ? 'bg-purple-500 f shadow-xl hover:text-white text-white' : 'text-black-700 hover:bg-indigo-100'
                                        )
                                    }
                                >
                                    {name}
                                </Tab>
                            ))}
                        </Tab.List>
                        <Tab.Panels className="max-w-5xl mt-2 py-1">
                            <div>
                                {tabs.map((tab, idx) => (
                                    <Tab.Panel key={idx} className="bg-white  focus:outline-none">
                                        {tab.component}
                                    </Tab.Panel>
                                ))}
                            </div>
                        </Tab.Panels>
                    </Tab.Group>
                </div>
            </Loading>
        </>
    );
};
